/**
 * Native (Capacitor) integration. Everything here no-ops on the web build, so
 * the same React app runs as a PWA and as the iOS/Android app. Wires:
 *   - status bar + splash screen polish
 *   - push notifications (the daily-drop retention lever) → token to the worker
 *   - native share sheet for the share-as-image PNG
 *   - crisp native haptics
 */
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { Haptics, ImpactStyle } from "@capacitor/haptics";
import { PushNotifications } from "@capacitor/push-notifications";
import { Share } from "@capacitor/share";
import { SplashScreen } from "@capacitor/splash-screen";
import { Style, StatusBar } from "@capacitor/status-bar";
import { getIdToken } from "./auth";
import { anonId, sendFeedback } from "./engagement";

export const isNativeApp = Capacitor.isNativePlatform();

const ENGAGEMENT_URL =
  import.meta.env.VITE_ENGAGEMENT_URL ?? "https://insnip-engagement.insnip-app.workers.dev";

// The push token must bind to the ACTIVE identity (account uid when signed in, else the device
// anon id), exactly like every other API call. A private device-only copy of this helper kept
// registering tokens under the device id, so for a signed-in reader push_tokens.u never matched
// the u on their reads, streaks and stances: their personalised pushes targeted a stranger.
// engagement.anonId owns that precedence; reuse it.

async function sendPushToken(token: string, platform: string): Promise<void> {
  try {
    // A signed-in reader proves the account uid with their Firebase ID token (the worker
    // requires it for an account uid); signed out, or if the refresh fails, the device id
    // goes on its own exactly as before.
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    try {
      const idToken = await getIdToken();
      if (idToken) headers.Authorization = `Bearer ${idToken}`;
    } catch {
      /* no Firebase session: register unauthenticated */
    }
    await fetch(`${ENGAGEMENT_URL}/push-token`, {
      method: "POST",
      headers,
      body: JSON.stringify({ u: anonId(), token, platform }),
      keepalive: true,
    });
  } catch {
    /* best-effort, push setup must never block the app */
  }
}

// Deep-link a tapped notification into the exact story/surface it points at. The OS hands the
// tap to the Capacitor plugin, never the WebView, so without this bridge the app opened on the
// plain feed and the pushed story was silently dropped. Plants the hash (the boot path reads
// location.hash and leads the feed with it) AND dispatches insnip:pushnav for the running app
// (App.tsx listens and jumps straight to the story). Same code as the app release branches.
function routePushTap(data: Record<string, unknown> | undefined): void {
  try {
    const url = typeof data?.url === "string" ? data.url : "";
    const cardId = typeof data?.cardId === "string" ? data.cardId : "";
    const kind = typeof data?.kind === "string" ? data.kind : "";
    let hash = "";
    const m = /#(.+)$/.exec(url);
    if (m) hash = m[1];
    else if (cardId) hash = `card=${cardId}`;
    else if (kind) hash = kind;
    if (hash) {
      // Setting hash to the same value does not refire hashchange, so force it: clear then set.
      if (window.location.hash === `#${hash}`) window.location.hash = "";
      window.location.hash = hash;
    }
    window.dispatchEvent(new CustomEvent("insnip:pushnav", { detail: { url, cardId, kind, hash } }));
  } catch {
    /* never let a deep-link tap crash the app */
  }
}

// The push listeners are global and must be attached exactly once (Capacitor keeps firing them
// across register() calls). This guard lets both the silent boot path and the explicit opt-in
// path call register() without stacking duplicate token posts or duplicate tap routing.
let pushListenersWired = false;
function wireNativePushListeners(): void {
  if (pushListenersWired) return;
  pushListenersWired = true;
  void PushNotifications.addListener("registration", (token) => {
    void sendPushToken(token.value, Capacitor.getPlatform());
  });
  void PushNotifications.addListener("registrationError", (err) => {
    // Still nothing for the reader to see, but it must reach US. This callback fired silently on
    // every iPhone for the life of the iOS app (no aps-environment entitlement), and the only
    // trace was a push_tokens table with zero ios rows, found on 2026-09-13. Files under the
    // feedback admin view; no new endpoint.
    const detail = String((err as { error?: unknown } | undefined)?.error ?? "unknown").slice(0, 300);
    void sendFeedback({
      kind: "general",
      category: "push_registration_error",
      message: `${Capacitor.getPlatform()}: ${detail}`,
      platform: Capacitor.getPlatform(),
    });
  });
  // Tap on a notification (app in background or foreground) -> deep-link into the story/surface.
  void PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
    routePushTap(action?.notification?.data as Record<string, unknown> | undefined);
  });
}

async function registerPush(): Promise<void> {
  try {
    // Opt-in only: never PROMPT for push at launch (App Store review best practice, and our
    // standing FCM opt-in design). Register silently only if the user already granted it in a
    // prior session; a fresh install sees no notification prompt until it is explicitly enabled.
    const perm = await PushNotifications.checkPermissions();
    if (perm.receive !== "granted") return;
    wireNativePushListeners();
    await PushNotifications.register();
  } catch {
    /* push unavailable, fine */
  }
}

/** Call once at startup. Safe on web (no-op). */
export async function initNativeApp(onResume?: () => void): Promise<void> {
  if (!isNativeApp) return;
  try {
    await StatusBar.setStyle({ style: Style.Dark }).catch(() => {});
    await SplashScreen.hide().catch(() => {});
    if (onResume) {
      App.addListener("resume", () => onResume());
    }
    await registerPush();
  } catch {
    /* never let native setup break boot */
  }
}

/** Native share of a rendered card image; returns false if not handled natively. */
export async function nativeShareFile(
  blob: Blob,
  fileName: string,
  title: string,
  text?: string,
  url?: string,
): Promise<boolean> {
  if (!isNativeApp) return false;
  try {
    const dataUrl: string = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    await Share.share({ title, text, url, files: [dataUrl], dialogTitle: "Share InSnip card" });
    return true;
  } catch {
    return false;
  }
}

/** Crisp native haptic; no-op on web (the web app uses navigator.vibrate already). */
export function nativeHaptic(style: "light" | "medium" | "heavy" = "light"): void {
  if (!isNativeApp) return;
  const map = { light: ImpactStyle.Light, medium: ImpactStyle.Medium, heavy: ImpactStyle.Heavy };
  void Haptics.impact({ style: map[style] }).catch(() => {});
}

/**
 * Sync the native status bar to the app's current background. Uses only the global
 * Capacitor bridge (no plugin imports), so this is a guaranteed no-op on the web:
 * when the bridge or the StatusBar plugin is absent, nothing happens.
 */
export function syncStatusBar(hexBg: string): void {
  try {
    if (typeof window === "undefined") return;
    const bridge = (window as unknown as { Capacitor?: { Plugins?: Record<string, unknown> } }).Capacitor;
    const StatusBar = bridge?.Plugins?.StatusBar as
      | {
          setBackgroundColor?: (opts: { color: string }) => Promise<void>;
          setStyle?: (opts: { style: string }) => Promise<void>;
        }
      | undefined;
    if (!StatusBar) return;
    // Normalise "#abc" / "#aabbcc" and derive relative luminance to pick the icon style:
    // a dark background wants the "DARK" style (light icons), and vice versa.
    const raw = hexBg.replace("#", "").trim();
    const full = raw.length === 3 ? raw.split("").map((c) => c + c).join("") : raw;
    if (!/^[0-9a-fA-F]{6}$/.test(full)) return;
    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);
    const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
    void StatusBar.setBackgroundColor?.({ color: `#${full.toLowerCase()}` })?.catch(() => {});
    void StatusBar.setStyle?.({ style: luminance > 0.5 ? "LIGHT" : "DARK" })?.catch(() => {});
  } catch {
    /* status bar polish must never break the web app */
  }
}

/**
 * One tap-feedback entry point for web AND native: prefer the Capacitor Haptics
 * plugin via the global bridge (crisp physical impact on device), else fall back
 * to navigator.vibrate. Everything guarded, silent no-op where unsupported.
 */
export function hapticTap(pattern: number[] = [10]): void {
  // Browsers ignore vibration until the user's first real interaction and Chrome
  // logs a console error for the attempt. A haptic nobody can feel is pure noise,
  // so skip it (dwell-triggered flourishes like the Snip can fire before any tap).
  try {
    const activation = (navigator as { userActivation?: { hasBeenActive?: boolean } }).userActivation;
    if (activation && activation.hasBeenActive === false) return;
  } catch {
    /* userActivation probing must never break the tap path */
  }
  try {
    if (typeof window !== "undefined") {
      const bridge = (window as unknown as { Capacitor?: { Plugins?: Record<string, unknown> } }).Capacitor;
      const HapticsPlugin = bridge?.Plugins?.Haptics as
        | { impact?: (opts: { style: string }) => Promise<void> }
        | undefined;
      if (HapticsPlugin?.impact) {
        void HapticsPlugin.impact({ style: "LIGHT" })?.catch(() => {});
        return;
      }
    }
  } catch {
    /* fall through to vibration */
  }
  try {
    if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
      navigator.vibrate(pattern);
    }
  } catch {
    /* vibration not allowed */
  }
}

/** The app's own notification switch. The website never runs native, so this is the stub the
 *  shared tools (src/more/AlertButton.tsx) compile against; the app branch carries the real one,
 *  which asks the OS and registers the device with the worker. */
export type NativePushState = "unsupported" | "prompt" | "denied" | "granted";
export async function enableNativePush(): Promise<NativePushState> {
  return "unsupported";
}
